import { useEffect, useRef, useState, type ReactNode } from "react";
import { GlossaryTerm } from "./GlossaryTerm";
import { track } from "@/lib/analytics";

/**
 * "One God… but thousands of gods?" — the hero interactive explainer.
 *
 * A 5-step gentle walkthrough with progress dots and Back/Continue.
 * Step 3 is interactive: pick an analogy to continue.
 * Step 5 is the landing line + reactions + (non-functional) share.
 *
 * Voice: warm, validating, never preachy, never absolute.
 */

type Analogy = "roles" | "ceo" | "water";

const ANALOGIES: {
  id: Analogy;
  emoji: string;
  title: string;
  body: string;
}[] = [
  {
    id: "roles",
    emoji: "👩‍👧",
    title: "One person, many roles",
    body: "You're one person, but a child to your parents, a friend to your friends, a boss at work. Same you, many forms. God is one; the forms are many.",
  },
  {
    id: "ceo",
    emoji: "🏢",
    title: "A CEO and the departments",
    body: "One source, approached through whichever \u201cdepartment\u201d you need — wealth, learning, courage. Still one organisation.",
  },
  {
    id: "water",
    emoji: "💧",
    title: "Water and the vessel",
    body: "The same water takes the shape of whatever cup you pour it into.",
  },
];

const TOTAL_STEPS = 5;

// ─── Glossary entries used inside this explainer ─────────────────────────────

const BrahmanTerm = ({ children }: { children: ReactNode }) => (
  <GlossaryTerm
    term="Brahman"
    iast="brahman"
    definition="The single ultimate reality — the ground of everything that exists. Not a person, not 'a god' among gods."
    englishNote="English 'God' usually means a big person somewhere; Brahman is closer to 'the very is-ness of everything.'"
  >
    {children}
  </GlossaryTerm>
);

const MurtiTerm = ({ children }: { children: ReactNode }) => (
  <GlossaryTerm
    term="Murti"
    iast="mūrti"
    definition="A sacred image or statue used as a focusing point for prayer — a window, not the thing itself."
    englishNote="'Idol' sounds like the object is being worshipped; murti is more like a photo of someone you love."
  >
    {children}
  </GlossaryTerm>
);

const SagunaTerm = ({ children }: { children: ReactNode }) => (
  <GlossaryTerm
    term="Saguna"
    iast="saguṇa"
    definition="The ultimate reality 'with form' — approachable as a person, with a name, story, and face."
    englishNote="English has no easy word for 'the same God but with a face you can talk to.'"
  >
    {children}
  </GlossaryTerm>
);

const NirgunaTerm = ({ children }: { children: ReactNode }) => (
  <GlossaryTerm
    term="Nirguna"
    iast="nirguṇa"
    definition="The ultimate reality 'without form' — beyond name, image, or description. Pure being."
    englishNote="'Formless' sounds empty; nirguna means 'too full to fit any one shape.'"
  >
    {children}
  </GlossaryTerm>
);

// ─── Main component ──────────────────────────────────────────────────────────

export const OneAndManyExplainer = () => {
  const [step, setStep] = useState(1);
  const [picked, setPicked] = useState<Analogy | null>(null);
  const [reaction, setReaction] = useState<"up" | "down" | null>(null);
  const [shared, setShared] = useState(false);
  const startedRef = useRef(false);
  const completedRef = useRef(false);
  const pickedRef = useRef<Analogy | null>(null);
  const reactionRef = useRef<"up" | "down" | null>(null);

  const canContinue = step !== 3 || picked !== null;
  const isLast = step === TOTAL_STEPS;

  const next = () => {
    if (isLast || !canContinue) return;
    if (!startedRef.current) {
      startedRef.current = true;
      track("explainer_started");
    }
    setStep((s) => s + 1);
  };
  const back = () => step > 1 && setStep((s) => s - 1);

  useEffect(() => {
    if (step === TOTAL_STEPS && !completedRef.current) {
      completedRef.current = true;
      track("explainer_completed");
    }
  }, [step]);

  useEffect(() => {
    if (picked && picked !== pickedRef.current) {
      pickedRef.current = picked;
      track("analogy_picked", { analogy: picked });
    }
  }, [picked]);

  useEffect(() => {
    if (reaction && reaction !== reactionRef.current) {
      reactionRef.current = reaction;
      track("explainer_feedback", { value: reaction });
    }
  }, [reaction]);

  return (
    <div className="relative">
      {/* Progress */}
      <div className="mb-8 flex items-center gap-3">
        <div className="flex flex-1 gap-1.5">
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => {
            const active = i + 1 <= step;
            return (
              <span
                key={i}
                className={`h-[3px] flex-1 rounded-full transition-all duration-500 ${
                  active ? "bg-primary" : "bg-border"
                }`}
                aria-hidden
              />
            );
          })}
        </div>
        <span className="font-display text-xs tracking-[0.18em] text-ink-soft">
          {String(step).padStart(2, "0")} / {String(TOTAL_STEPS).padStart(2, "0")}
        </span>
      </div>

      {/* Card */}
      <div className="relative overflow-hidden rounded-3xl border border-border bg-card/90 p-7 shadow-[0_1px_0_hsl(var(--accent)/0.18)_inset,0_18px_40px_-30px_hsl(24_25%_15%/0.3)] sm:p-10">
        {step === 1 && <Step1 />}
        {step === 2 && (
          <Step2 BrahmanTerm={BrahmanTerm} SagunaTerm={SagunaTerm} NirgunaTerm={NirgunaTerm} />
        )}
        {step === 3 && <Step3 picked={picked} setPicked={setPicked} />}
        {step === 4 && <Step4 MurtiTerm={MurtiTerm} />}
        {step === 5 && (
          <Step5
            reaction={reaction}
            setReaction={setReaction}
            shared={shared}
            setShared={setShared}
            MurtiTerm={MurtiTerm}
          />
        )}
      </div>

      {/* Controls */}
      <div className="mt-7 flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={back}
          disabled={step === 1}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm text-ink-soft transition-colors enabled:hover:text-foreground disabled:opacity-30"
        >
          <span aria-hidden>←</span> Back
        </button>

        {!isLast ? (
          <button
            type="button"
            onClick={next}
            disabled={!canContinue}
            className="group inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition-all enabled:hover:shadow-md disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none"
            aria-label={step === 3 && !canContinue ? "Pick an analogy to continue" : "Continue"}
          >
            Continue
            <span className="transition-transform group-enabled:group-hover:translate-x-0.5">
              →
            </span>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setStep(1)}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:border-foreground/40"
          >
            Start again
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Individual steps ────────────────────────────────────────────────────────

const StepKicker = ({ children }: { children: ReactNode }) => (
  <p className="mb-4 text-[11px] uppercase tracking-[0.22em] text-accent">
    {children}
  </p>
);

const Step1 = () => (
  <div>
    <StepKicker>You're not on the spot</StepKicker>
    <p className="font-display text-2xl font-light leading-snug text-foreground sm:text-3xl">
      If that has ever confused you —{" "}
      <span className="italic text-primary">you're not alone</span>. It confuses
      people who've been Hindu their whole lives too.
    </p>
    <p className="mt-5 text-base leading-relaxed text-ink-soft">
      There's no test here. No right answer to memorise. Just a question worth
      sitting with for a minute.
    </p>
  </div>
);

const Step2 = ({
  BrahmanTerm: Brahman,
  SagunaTerm: Saguna,
  NirgunaTerm: Nirguna,
}: {
  BrahmanTerm: typeof BrahmanTerm;
  SagunaTerm: typeof SagunaTerm;
  NirgunaTerm: typeof NirgunaTerm;
}) => (
  <div>
    <StepKicker>The reframe</StepKicker>
    <p className="font-display text-2xl font-light leading-snug text-foreground sm:text-3xl">
      Most Hindu thought points to{" "}
      <span className="italic text-primary">one ultimate reality</span> —
      not one old man in the sky, but the single ground of everything.
    </p>
    <p className="mt-5 text-base leading-relaxed text-ink-soft">
      The many gods aren't rivals; they're <em>forms</em> — different faces of
      that one reality. Some traditions call this <Brahman>brahman</Brahman>,
      met either <Saguna>with form</Saguna> or <Nirguna>without form</Nirguna>.
    </p>
  </div>
);

const Step3 = ({
  picked,
  setPicked,
}: {
  picked: Analogy | null;
  setPicked: (a: Analogy) => void;
}) => (
  <div>
    <StepKicker>Pick what lands for you</StepKicker>
    <p className="font-display text-xl font-light leading-snug text-foreground sm:text-2xl">
      Pick the comparison that lands for you —{" "}
      <span className="italic text-primary">there's no wrong choice</span>.
    </p>

    <div className="mt-7 grid gap-3">
      {ANALOGIES.map((a) => {
        const isPicked = picked === a.id;
        const isDimmed = picked !== null && !isPicked;
        return (
          <button
            key={a.id}
            type="button"
            onClick={() => setPicked(a.id)}
            aria-pressed={isPicked}
            className={`group relative rounded-2xl border p-5 text-left transition-all duration-300 ${
              isPicked
                ? "border-primary/60 bg-terracotta-soft/40 shadow-[0_8px_24px_-18px_hsl(var(--primary)/0.5)]"
                : isDimmed
                ? "border-border bg-card/40 opacity-50 hover:opacity-70"
                : "border-border bg-card hover:border-foreground/30"
            }`}
          >
            <div className="flex items-start gap-4">
              <span className="text-2xl leading-none" aria-hidden>
                {a.emoji}
              </span>
              <div className="flex-1">
                <p className="font-display text-lg font-medium text-foreground">
                  {a.title}
                </p>
                <div
                  className={`grid transition-all duration-300 ${
                    isPicked
                      ? "mt-2 grid-rows-[1fr] opacity-100"
                      : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <p className="overflow-hidden text-sm leading-relaxed text-ink-soft">
                    {a.body}
                  </p>
                </div>
              </div>
            </div>
          </button>
        );
      })}
    </div>

    {!picked && (
      <p className="mt-5 text-xs italic text-muted-foreground">
        Tap one to continue.
      </p>
    )}
  </div>
);

const Step4 = ({ MurtiTerm: Murti }: { MurtiTerm: typeof MurtiTerm }) => (
  <div>
    <StepKicker>About the statues</StepKicker>
    <p className="font-display text-2xl font-light leading-snug text-foreground sm:text-3xl">
      So why pray to a statue?
    </p>
    <p className="mt-5 text-base leading-relaxed text-ink-soft">
      The image is a <Murti>murti</Murti> — a focusing point, not the god
      itself. Like a photo of someone you love: you're reaching the person
      through it, not worshipping paper.
    </p>
  </div>
);

const Step5 = ({
  reaction,
  setReaction,
  shared,
  setShared,
  MurtiTerm: Murti,
}: {
  reaction: "up" | "down" | null;
  setReaction: (r: "up" | "down") => void;
  shared: boolean;
  setShared: (b: boolean) => void;
  MurtiTerm: typeof MurtiTerm;
}) => (
  <div>
    <StepKicker>The one-liner to keep</StepKicker>

    <blockquote className="relative rounded-2xl border border-accent/40 bg-paper-warm/60 p-6 sm:p-7">
      <span
        aria-hidden
        className="absolute -top-3 left-5 font-display text-4xl leading-none text-accent"
      >
        &ldquo;
      </span>
      <p className="font-display text-xl font-light italic leading-snug text-foreground sm:text-2xl">
        Hindus see one ultimate reality with many forms — like one person who's
        a daughter, a friend, and a boss. The <Murti>murti</Murti> is a focus
        point, not the god itself.
      </p>
    </blockquote>

    <div className="mt-7 flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <span className="text-xs uppercase tracking-[0.18em] text-ink-soft">
          Did this click?
        </span>
        <div className="flex gap-2">
          {(["up", "down"] as const).map((r) => {
            const active = reaction === r;
            return (
              <button
                key={r}
                type="button"
                onClick={() => setReaction(r)}
                aria-pressed={active}
                aria-label={r === "up" ? "Yes, this clicked" : "Not quite"}
                className={`flex h-9 w-9 items-center justify-center rounded-full border text-base transition-all ${
                  active
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card text-ink-soft hover:border-foreground/40"
                }`}
              >
                {r === "up" ? "👍" : "👎"}
              </button>
            );
          })}
        </div>
      </div>

      <button
        type="button"
        onClick={() => {
          setShared(true);
          setTimeout(() => setShared(false), 1800);
        }}
        className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm text-foreground transition-colors hover:border-foreground/40"
      >
        {shared ? "✓ Copied" : "Share this"}
      </button>
    </div>

    {reaction && (
      <p className="mt-5 text-sm italic text-ink-soft">
        {reaction === "up"
          ? "Glad it landed. There's more like this when you're ready."
          : "Totally fair. Some ideas need a different door — we'll keep trying."}
      </p>
    )}
  </div>
);

export default OneAndManyExplainer;
